import type { RolloverPreview, RolloverScopeCounts, ScopeType } from '../types/strategicKpis';

/** The four target levels in the order the rollover panel lists them. */
export const ROLLOVER_SCOPES: readonly ScopeType[] = ['PERSON', 'TEAM', 'COMPANY', 'BU'];

/** Labels of the levels in the rollover panel. */
export const ROLLOVER_SCOPE_LABEL: Record<ScopeType, string> = {
    PERSON: 'Persons', TEAM: 'Teams', COMPANY: 'Companies', BU: 'Business units',
};

/** null = valid; otherwise the message the panel shows. The server refuses the same case with a 400. */
export function validateRolloverYears(sourceYear: number, targetYear: number): string | null {
    if (!Number.isInteger(sourceYear) || !Number.isInteger(targetYear)) return 'Choose both years.';
    if (targetYear <= sourceYear) return 'The target year must be later than the source year.';
    return null;
}

/** All four levels (or none) = no `scopeTypes` argument (the server default); otherwise the chosen ones in fixed order. */
export function rolloverScopeArg(scopes: ReadonlySet<ScopeType>): ScopeType[] | undefined {
    if (scopes.size === 0 || scopes.size === ROLLOVER_SCOPES.length) return undefined;
    return ROLLOVER_SCOPES.filter(s => scopes.has(s));
}

/**
 * Years offered in the two selects: exactly the years the page's year filter can show (firstYear .. maxYear), so a
 * target year the page cannot display and a source year the server answers with a 400 are never offered.
 */
export function rolloverYearOptions(firstYear: number, maxYear: number): number[] {
    const out: number[] = [];
    for (let y = firstYear; y <= maxYear; y++) out.push(y);
    return out;
}

/** A default year pulled into firstYear .. maxYear (the selects offer nothing else). */
export function clampRolloverYear(year: number, firstYear: number, maxYear: number): number {
    return Math.min(Math.max(year, firstYear), maxYear);
}

export interface RolloverRow extends RolloverScopeCounts {
    scope: ScopeType;
    label: string;
}

/** One row per chosen level, in fixed order, straight from byScope (a missing key counts as zero). */
export function rolloverRows(preview: Pick<RolloverPreview, 'byScope'>, scopes: ReadonlySet<ScopeType>): RolloverRow[] {
    return ROLLOVER_SCOPES.filter(s => scopes.has(s)).map(scope => {
        const c = preview.byScope[scope] ?? { inserted: 0, skippedExisting: 0, skippedInactive: 0 };
        return { scope, label: ROLLOVER_SCOPE_LABEL[scope], inserted: c.inserted, skippedExisting: c.skippedExisting, skippedInactive: c.skippedInactive };
    });
}

/**
 * Column sums over the shown rows. The columns are never added to each other: the skipped counters
 * overlap with `skippedInactiveKpi` (an inactive KPI on a taken key counts in both).
 */
export function rolloverTotals(rows: readonly RolloverRow[]): RolloverScopeCounts {
    return rows.reduce<RolloverScopeCounts>((t, r) => ({
        inserted: t.inserted + r.inserted,
        skippedExisting: t.skippedExisting + r.skippedExisting,
        skippedInactive: t.skippedInactive + r.skippedInactive,
    }), { inserted: 0, skippedExisting: 0, skippedInactive: 0 });
}

/** Whether the copy step may start: edit right, valid years, a loaded preview that would copy something. */
export function canStartCopy(canEdit: boolean, yearsError: string | null, preview: Pick<RolloverPreview, 'byScope'> | null, scopes: ReadonlySet<ScopeType>): boolean {
    if (!canEdit || yearsError !== null || preview === null) return false;
    return rolloverTotals(rolloverRows(preview, scopes)).inserted > 0;
}

/** Plural-aware count text: `1 target`, `160 targets`. */
export function targetsText(n: number): string {
    return `${n} ${n === 1 ? 'target' : 'targets'}`;
}
